import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { wordService, userService } from '../services';
import { isCapacitor } from '../utils/platform';
import useNotification from '../components/Notifications/useNotifications';
import Notification from '../components/Notifications/Notification';
import HoverTooltip from '../components/tooltips/HoverTooltip';
import Button from '../components/buttons/Button';
import BackButton from '../components/buttons/BackButton';
import Card from '../components/Card/Card';
import Picker from '../components/inputs/Picker';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { formatDisplayName } from '../models/ReportTypeDesignHelper';
import { createLogger } from '../services/Debug';
import SkeletonManager from '../components/loaders/SkeletonManager';
import './Templates.css';

// Icons
import { 
  FaFileImport,
  FaFileUpload,
  FaDownload,
  FaTrash,
  FaBuilding,
  FaFileWord,
  FaLightbulb,
  FaQuestionCircle, 
  FaImage
} from 'react-icons/fa';
import InfoBanner from '../components/InfoBanner/InfoBanner';
import TutorialModal from '../components/TutorialModal/TutorialModal';
import TemplateBuilderWizard from './TemplateBuilder/TemplateBuilderWizard';

function Templates() {
  const log = createLogger('Templates');
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  const { profile, refreshProfile } = useAuth();

  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  
  // State
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [exampleTemplates, setExampleTemplates] = useState([]);
  const [selectedExampleId, setSelectedExampleId] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [exampleTemplatesLoading, setExampleTemplatesLoading] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [extractedReportTypes, setExtractedReportTypes] = useState([]);
  const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState(false);
  // User logo state
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoUrlRef = React.useRef('');
  const logoFileInputRef = React.useRef(null);
  // Default template state
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [defaultTemplateLoading, setDefaultTemplateLoading] = useState(false);
  const [defaultTemplateError, setDefaultTemplateError] = useState('');
  const defaultTemplateFileInputRef = React.useRef(null);
  // Template upload modal state
  const [pendingTemplateFile, setPendingTemplateFile] = useState(null);
  const [showReportTypeModal, setShowReportTypeModal] = useState(false);
  const [selectedReportTypeForUpload, setSelectedReportTypeForUpload] = useState('');



  // Get available report types using the same pattern
  const availableReportTypes = useMemo(() => {
    const sanitize = (types) => (Array.isArray(types) ? types.filter((t) => t && t !== 'all') : []);
    const favorites = profile?.reportTypeFavorites?.getFavorites?.() || [];
    if (Array.isArray(favorites) && favorites.length > 0) return sanitize(favorites);
    return sanitize(extractedReportTypes);
  }, [profile?.reportTypeFavorites, extractedReportTypes]);

  // Get only non-enterprise report types for user templates
  const userReportTypes = useMemo(() => {
    if (!profile?.reportTypeFavorites) return availableReportTypes;
    return availableReportTypes.filter(typeName => !profile.reportTypeFavorites.isEnterprise(typeName));
  }, [availableReportTypes, profile?.reportTypeFavorites]);

  // Gold/Enterprise gating
  const isGoldOrEnterprise = useMemo(() => {
    return Boolean(
      profile?.isGoldOrEnterprise === true ||
      profile?.isgoldorenterprise === true ||
      profile?.subscription?.planType === 'gold' ||
      profile?.subscription?.planType === 'enterprise' ||
      profile?.subscription?.plan === 'gold' ||
      profile?.subscription?.plan === 'enterprise'
    );
  }, [profile]);
  const gatingText = 'Réservé aux abonnements Gold et Entreprise';

  // Check if user is enterprise admin
  useEffect(() => {
    const checkAdminStatus = () => {
      setIsEnterpriseAdmin(profile?.isEnterpriseAdmin === true);
    };
    checkAdminStatus();
  }, [profile]);

  // Load user logo once on mount
  const loadUserLogo = useCallback(async () => {
    setLogoLoading(true);
    setLogoError('');
    try {
      const blob = await userService.getUserLogo();
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        logoUrlRef.current = url;
        setLogoUrl(url);
      } else {
        setLogoUrl('');
      }
    } catch (e) {
      // Not found or error -> no logo
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (err) {}
        logoUrlRef.current = '';
      }
      setLogoUrl('');
    } finally {
      setLogoLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserLogo();
    return () => {
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
    };
  }, [loadUserLogo]);

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setLogoError('');
    if (!file.type.startsWith('image/')) {
      setLogoError('Le fichier doit être une image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('Taille maximale 5 Mo.');
      return;
    }
    setLogoLoading(true);
    try {
      await userService.uploadUserLogo(file);
      await loadUserLogo();
    } catch (e) {
      setLogoError("Échec du téléchargement du logo.");
    } finally {
      setLogoLoading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm("Supprimer le logo du compte ?")) return;
    setLogoLoading(true);
    setLogoError('');
    try {
      await userService.deleteUserLogo();
      if (logoUrlRef.current) {
        try { window.URL.revokeObjectURL(logoUrlRef.current); } catch (e) {}
        logoUrlRef.current = '';
      }
      setLogoUrl('');
    } catch (e) {
      setLogoError('Échec de la suppression du logo.');
    } finally {
      setLogoLoading(false);
    }
  };

  // Load templates on mount
  useEffect(() => {
    // Set loading immediately on mount with 500ms minimum
    setIsInitialLoading(true);
      
    
    const initializeData = async () => {
      const minimumLoadingTime = 500;
      const startTime = Date.now();
      
      try {
        await Promise.all([
          loadTemplates(),
          loadExampleTemplates(),
          loadReportTypes(),
          loadDefaultTemplate()
        ]);
        
        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
        
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
      } finally {
        setIsInitialLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // Load report types
  const loadReportTypes = useCallback(async () => {
    try {
      const types = profile.getFavorites;
      setExtractedReportTypes(types);
    } catch (error) {
      log.error('Error loading report types:', error);
    }
  }, []);

  // Template management functions
  const loadTemplates = useCallback(async () => {
    setTemplateLoading(true);
    setTemplateError('');
    try {
      const userTemplates = await wordService.getUserTemplates();
      log.debug('User templates:', userTemplates);
      // Filter out system templates (only show user templates)
      const userOnlyTemplates = userTemplates.filter(template => template.userId !== null);
      
      setTemplates(userOnlyTemplates || []);
    } catch (error) {
      log.error('Error loading templates:', error);
      setTemplateError('Impossible de charger les templates');
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  const loadExampleTemplates = useCallback(async () => {
    setExampleTemplatesLoading(true);
    try {
      const examples = await wordService.getExampleTemplates();
      setExampleTemplates(examples || []);
    } catch (error) {
      log.error('Error loading example templates:', error);
      setExampleTemplates([]);
    } finally {
      setExampleTemplatesLoading(false);
    }
  }, []);

  const uploadTemplate = useCallback(async (file, reportTypeName = null) => {
    setTemplateLoading(true);
    setTemplateError('');
    try {
      // availableReportTypes is an array of string names, not objects
      // reportTypeName is the string name of the report type (e.g., "radiologie")

      // Upload with report type included in FormData (single API call)
      await wordService.uploadUserTemplate(file, null, reportTypeName);
      showNotification('Template téléchargé avec succès!', 'success');

      // Refresh templates list
      await loadTemplates();
    } catch (error) {
      log.error('Error uploading template:', error);
      setTemplateError('Erreur lors du téléchargement du template');
      showNotification('Erreur lors du téléchargement du template', 'error');
      throw error;
    } finally {
      setTemplateLoading(false);
    }
  }, [loadTemplates, showNotification]);

  // Handle when user selects a template file - opens modal to choose report type
  const handleTemplateFileSelected = useCallback((file) => {
    setPendingTemplateFile(file);
    // Set first available report type as default (excluding those that already have templates)
    const availableTypes = userReportTypes.filter(typeName => !templates.some(t => t.reportType === typeName));
    const firstReportType = availableTypes.length > 0 ? availableTypes[0] : '';
    setSelectedReportTypeForUpload(firstReportType);
    setShowReportTypeModal(true);
  }, [userReportTypes, templates]);

  // Confirm upload with selected report type
  const confirmTemplateUpload = useCallback(async () => {
    if (!pendingTemplateFile) return;

    try {
      await uploadTemplate(pendingTemplateFile, selectedReportTypeForUpload);
      // Close modal and reset state on success
      setShowReportTypeModal(false);
      setPendingTemplateFile(null);
      setSelectedReportTypeForUpload('');
    } catch (error) {
      // Error already handled in uploadTemplate
      // Keep modal open so user can retry or cancel
    }
  }, [pendingTemplateFile, selectedReportTypeForUpload, uploadTemplate]);

  // Cancel template upload
  const cancelTemplateUpload = useCallback(() => {
    setShowReportTypeModal(false);
    setPendingTemplateFile(null);
    setSelectedReportTypeForUpload('');
  }, []);

  const deleteTemplate = useCallback(async (templateId) => {
    setTemplateLoading(true);
    setTemplateError('');
    try {
      await wordService.deleteUserTemplate(templateId);
      
      // Remove the deleted template from the list
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      setTemplates(updatedTemplates);
      showNotification('Template supprimé avec succès!', 'success');
    } catch (error) {
      log.error('Error deleting template:', error);
      setTemplateError('Erreur lors de la suppression du template');
      showNotification('Erreur lors de la suppression du template', 'error');
      throw error;
    } finally {
      setTemplateLoading(false);
    }
  }, [templates, showNotification]);

  const updateTemplateReportType = useCallback(async (template, reportType) => {
    setTemplateLoading(true);
    setTemplateError('');
    try {
      await wordService.updateUserTemplate(template.id, {
        reportType: reportType
      });
      
      // Update the template in the list
      setTemplates(prev => prev.map(t => 
        t.id === template.id ? { ...t, reportType } : t
      ));
      
      showNotification('Template mis à jour avec succès!', 'success');
    } catch (error) {
      log.error('Error updating report type:', error);
      setTemplateError('Erreur lors de la mise à jour du template');
      showNotification('Erreur lors de la mise à jour du template', 'error');
      throw error;
    } finally {
      setTemplateLoading(false);
    }
  }, [showNotification]);

  const downloadTemplate = useCallback(async (template) => {
    setTemplateLoading(true);
    setTemplateError('');
    try {
      const blob = await wordService.downloadUserTemplate(template.id);
      
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${template.name || 'template'}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showNotification('Template téléchargé avec succès!', 'success');
      } else {
        throw new Error('Invalid blob response');
      }
    } catch (error) {
      log.error('Error downloading template:', error);
      setTemplateError('Erreur lors du téléchargement du template');
      showNotification('Erreur lors du téléchargement du template', 'error');
      throw error;
    } finally {
      setTemplateLoading(false);
    }
  }, [showNotification]);

  const downloadExampleTemplate = useCallback(async (exampleId) => {
    if (!exampleId) return;
    
    setTemplateLoading(true);
    setTemplateError('');
    try {
      const blob = await wordService.downloadExampleTemplate(exampleId);
      
      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${exampleId.replace('example+', '')}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showNotification('Template d\'exemple téléchargé avec succès!', 'success');
      } else {
        throw new Error('Invalid blob response');
      }
    } catch (error) {
      log.error('Error downloading example template:', error);
      setTemplateError('Erreur lors du téléchargement du template d\'exemple');
      showNotification('Erreur lors du téléchargement du template d\'exemple', 'error');
      throw error;
    } finally {
      setTemplateLoading(false);
    }
  }, [showNotification]);

  // Default template management functions
  const loadDefaultTemplate = useCallback(async () => {
    setDefaultTemplateLoading(true);
    setDefaultTemplateError('');
    try {
      const template = await wordService.getUserDefaultTemplate();
      setDefaultTemplate(template); // Can be null if no default template exists
    } catch (error) {
      log.error('Error loading default template:', error);
      setDefaultTemplateError('Impossible de charger le template par défaut');
      setDefaultTemplate(null);
    } finally {
      setDefaultTemplateLoading(false);
    }
  }, []);

  const uploadDefaultTemplate = useCallback(async (file) => {
    if (!file) return;

    setDefaultTemplateError('');

    // Validate file type
    if (!file.name.endsWith('.docx')) {
      setDefaultTemplateError('Le fichier doit être un document .docx');
      showNotification('Le fichier doit être un document .docx', 'error');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setDefaultTemplateError('Taille maximale 10 Mo.');
      showNotification('Le fichier ne doit pas dépasser 10 Mo', 'error');
      return;
    }

    setDefaultTemplateLoading(true);
    try {
      const template = await wordService.uploadUserDefaultTemplate(file);
      setDefaultTemplate(template);
      showNotification('Template par défaut téléchargé avec succès!', 'success');
    } catch (error) {
      log.error('Error uploading default template:', error);
      setDefaultTemplateError('Échec du téléchargement du template par défaut.');
      showNotification('Erreur lors du téléchargement du template', 'error');
    } finally {
      setDefaultTemplateLoading(false);
    }
  }, [showNotification]);

  const deleteDefaultTemplate = useCallback(async () => {
    if (!window.confirm('Supprimer le template par défaut ? Cette action est irréversible.')) {
      return;
    }

    setDefaultTemplateLoading(true);
    setDefaultTemplateError('');
    try {
      await wordService.deleteUserDefaultTemplate();
      setDefaultTemplate(null);
      showNotification('Template par défaut supprimé avec succès!', 'success');
    } catch (error) {
      log.error('Error deleting default template:', error);
      setDefaultTemplateError('Échec de la suppression du template par défaut.');
      showNotification('Erreur lors de la suppression du template', 'error');
    } finally {
      setDefaultTemplateLoading(false);
    }
  }, [showNotification]);

  const downloadSystemDefaultTemplate = useCallback(async () => {
    setDefaultTemplateLoading(true);
    try {
      const blob = await wordService.downloadSystemDefaultTemplate();

      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'template_systeme_defaut.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showNotification('Template système téléchargé avec succès!', 'success');
      } else {
        throw new Error('Aucun fichier retourné');
      }
    } catch (error) {
      log.error('Error downloading system default template:', error);
      showNotification('Erreur lors du téléchargement du template système', 'error');
    } finally {
      setDefaultTemplateLoading(false);
    }
  }, [showNotification]);

    // Early return if still loading or no profile
    if (isInitialLoading || templateLoading || exampleTemplatesLoading || !profile) {
      return (
        <SkeletonManager  active={true} />
      );
    }

  return (
    <div className="templates-container">
      <BackButton />
      
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}

      {templateLoading ? (
        <Card className="card-large">
          <div className="template-loading">
            <div className="spinner"></div>
            <p>Chargement des templates...</p>
          </div>
        </Card>
      ) : (
        <>
  {/* Default Template */}
  <Card className="card-large">
            <SectionHeader icon={FaFileWord} title="Template Word par défaut" variant="default" />
            <InfoBanner icon={FaLightbulb} title="À propos du template par défaut">
              Le template par défaut est utilisé pour tous vos rapports qui n'ont pas de template spécifique associé. <br />
              Créez un document Word (.docx) avec votre entête, logos, signature, etc. et incluez le placeholder <strong>{'{rapport}'}</strong> à l'endroit où le rapport doit être inséré.
            </InfoBanner>

            {defaultTemplate ? (
              <div className="template-default-info">
                <p><strong>Template actuel :</strong> {defaultTemplate.name}</p>
                <p><strong>Téléchargé le :</strong> {new Date(defaultTemplate.createdAt).toLocaleDateString('fr-FR')}</p>
                <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                  <input
                    ref={defaultTemplateFileInputRef}
                    type="file"
                    accept=".docx"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (file) {
                        uploadDefaultTemplate(file);
                        e.target.value = '';
                      }
                    }}
                    disabled={defaultTemplateLoading}
                  />
                  <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                    <span>
                      <Button
                        variant="primary"
                        icon={FaFileUpload}
                        onClick={() => defaultTemplateFileInputRef.current && defaultTemplateFileInputRef.current.click()}
                        disabled={!isGoldOrEnterprise || defaultTemplateLoading}
                      >
                        Remplacer le template
                      </Button>
                    </span>
                  </HoverTooltip>
                  <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                    <span>
                      <Button
                        variant="danger"
                        icon={FaTrash}
                        onClick={deleteDefaultTemplate}
                        disabled={!isGoldOrEnterprise || defaultTemplateLoading}
                      >
                        Supprimer
                      </Button>
                    </span>
                  </HoverTooltip>
                </div>
              </div>
            ) : (
              <div className="template-default-empty">
                <p>Aucun template par défaut configuré.</p>
                <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <input
                    ref={defaultTemplateFileInputRef}
                    type="file"
                    accept=".docx"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (file) {
                        uploadDefaultTemplate(file);
                        e.target.value = '';
                      }
                    }}
                    disabled={defaultTemplateLoading}
                  />
                  <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                    <span>
                      <Button
                        variant="primary"
                        icon={FaFileUpload}
                        onClick={() => defaultTemplateFileInputRef.current && defaultTemplateFileInputRef.current.click()}
                        disabled={!isGoldOrEnterprise || defaultTemplateLoading}
                      >
                        Ajouter un template par défaut
                      </Button>
                    </span>
                  </HoverTooltip>
                </div>
              </div>
            )}

            {defaultTemplateError && (
              <div className="enterprise-admin-template-error" style={{ marginTop: 8 }}>{defaultTemplateError}</div>
            )}

          
          </Card>


          {/* User Templates */}
          <Card className="card-large">
            <SectionHeader icon={FaFileWord} title={`Vos templates Word   spécifiques (${templates.length}/5)`} variant="default" />
            {/* Upload new template inside the same card */}
            <InfoBanner icon={FaLightbulb} title="Télécharger un nouveau template">
              Les tempates spécifiques sont liés à un modèle de rapport. Préparez un document Word (.docx) avec vos styles (titres, paragraphes, en-têtes/pieds) et uploadez le ici. <br/>
              Utilisez notre outil de création de template pur créer un template personnalisé à partir d'un modèle rapport.
            </InfoBanner>
            <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input
                type="file"
                accept=".docx"
                style={{ display: 'none' }}
                ref={(el) => { Templates._templateInputRef = el; }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    handleTemplateFileSelected(file);
                    e.target.value = '';
                  }
                }}
                disabled={templates.length >= 5 || templateLoading}
              />
              <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                <span>
                  <Button 
                    variant="primary"
                    icon={FaFileUpload}
                    onClick={() => { try { Templates._templateInputRef && Templates._templateInputRef.click(); } catch (e) {} }}
                    disabled={!isGoldOrEnterprise || templates.length >= 5 || templateLoading}
                  >
                    Uploader un template
                  </Button>
                </span>
              </HoverTooltip>
              {!isCapacitor && (
                <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                  <span>
                    <Button
                      variant="word"
                      onClick={() => setIsBuilderOpen(true)}
                      icon={FaFileWord}
                      disabled={!isGoldOrEnterprise}
                    >
                      Créer un template
                    </Button>
                  </span>
                </HoverTooltip>
              )}

                <span>
                  <Button
                    variant="info"
                    onClick={() => setIsGuideOpen(true)}
                    icon={FaQuestionCircle}
                  >
                    Aide
                  </Button>
                </span>
              {templates.length >= 5 && (
                <p className="template-limit-warning">
                  Limite de 5 templates atteinte. Supprimez un template existant pour en ajouter un nouveau.
                </p>
              )}
            </div>
            <div className="template-list-section">
              {templates.length === 0 ? (
                <p className="template-empty-message">
                  Aucun template personnalisé. Téléchargez un template exemple ou créez le vôtre pour une personnalisation avancée.
                </p>
              ) : (
                <div className="template-list">
                  {templates.map((template) => (
                    <div key={template.id} className="template-item">
                      <span className="template-name">{template.name}</span>
                      <div className="template-controls">
                        <div className="template-associated-label">
                          Modèle associé:
                        </div>
                        <select
                          value={template.reportType && template.reportType !== 'all' ? template.reportType : (userReportTypes.length > 0 ? userReportTypes[0] : '')}
                          onChange={(e) => updateTemplateReportType(template, e.target.value || null)}
                          className="template-report-type-select"
                        >
                          {userReportTypes.map(typeName => (
                            <option key={typeName} value={typeName}>
                              {formatDisplayName(typeName)}
                            </option>
                          ))}
                        </select>
                        <div className="template-actions">
                        <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                          <span>
                            <Button
                              variant="info"
                              icon={FaDownload}
                              onClick={() => downloadTemplate(template)}
                              disabled={templateLoading}
                              title="Télécharger"
                            />
                          </span>
                        </HoverTooltip>
                        <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                          <span>
                            <Button
                              variant="danger"
                              icon={FaTrash}
                              onClick={() => {
                                if (window.confirm('Êtes-vous sûr de vouloir supprimer ce template?')) {
                                  deleteTemplate(template.id);
                                }
                              }}
                              disabled={!isGoldOrEnterprise || templateLoading}
                              title="Supprimer"
                            />
                          </span>
                        </HoverTooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>


          {/* User Logo Section */}
          <Card className="card-large">
            <SectionHeader icon={FaImage} title="Logo personnel" variant="default" />
            <InfoBanner>
              Ce logo apparaît dans l'en-tête de vos rapports exportés qui utilisent un template par défaut (par exemple: le template d'ETT)
            </InfoBanner>
            <div className="template-upload-section" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{
                width: 140,
                height: 80,
                border: '1px solid #ddd',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fafafa',
                overflow: 'hidden'
              }}>
                {logoLoading ? (
                  <span>Chargement…</span>
                ) : logoUrl ? (
                  <img src={logoUrl} alt="Logo personnel" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ color: '#888', fontSize: 12 }}>Aucun logo</span>
                )}
              </div>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) handleUploadLogo(file);
                  e.target.value = '';
                }}
                disabled={logoLoading}
              />
              <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
              <Button
                variant="primary"
                icon={FaFileUpload}
                disabled={logoLoading || !isGoldOrEnterprise}
                onClick={() => { try { logoFileInputRef.current && logoFileInputRef.current.click(); } catch (e) {} }}
              >
                {logoUrl ? 'Remplacer le logo' : 'Ajouter un logo'}
              </Button>
              </HoverTooltip>
              <Button
                variant="danger"
                icon={FaTrash}
                onClick={handleDeleteLogo}
                disabled={logoLoading || !isGoldOrEnterprise || !logoUrl}
              >
                Supprimer
              </Button>
            </div>
            {logoError && (
              <div className="enterprise-admin-template-error" style={{ marginTop: 8 }}>{logoError}</div>
            )}
            <p style={{ marginTop: 8, color: '#666', fontSize: 12 }}>Formats images uniquement (.png, .jpg, .jpeg), taille maximale 5 Mo. Un seul logo à la fois.</p>
          </Card>


        

          {/* Example Templates */}
          <Card className="card-large">
            <SectionHeader icon={FaFileImport} title="Templates exemples" variant="default" />
            <InfoBanner>
              Téléchargez un template exemple, adaptez la mise en page dans Word, puis uploadez votre version personnalisée dans la section « Vos templates spécifiques ».
            </InfoBanner>
            <div className="template-system-section">
              <p>Sélectionnez et téléchargez un template exemple pour l'utiliser comme base.</p>
              {exampleTemplatesLoading ? (
                <div className="template-loading">
                  <div className="spinner"></div>
                  <p>Chargement des templates exemples...</p>
                </div>
              ) : exampleTemplates.length > 0 ? (
                <div className="template-example-list">
                  <select
                    value={selectedExampleId}
                    onChange={(e) => setSelectedExampleId(e.target.value)}
                    className="template-example-select"
                  >
                    <option value="">-- Sélectionnez un template --</option>
                    {exampleTemplates.map((example) => (
                      <option key={example.id} value={example.id}>
                        {formatDisplayName(example.reportType)}
                      </option>
                    ))}
                  </select>
                  <HoverTooltip text={!isGoldOrEnterprise ? gatingText : ''} disabled={isGoldOrEnterprise}>
                    <span>
                      <Button
                        variant="primary"
                        icon={FaDownload}
                        onClick={() => downloadExampleTemplate(selectedExampleId)}
                        disabled={!selectedExampleId || templateLoading}
                      >
                        Télécharger l'exemple
                      </Button>
                    </span>
                  </HoverTooltip>
                </div>
              ) : (
                <div className="template-example-empty">
                  <p>Aucun template d'exemple n'est disponible actuellement.</p>
                </div>
              )}
            </div>
          </Card>


          

          {/* Enterprise Admin Section */}
          {isEnterpriseAdmin && (
            <Card className="card-large">
              <SectionHeader icon={FaBuilding} title="Gestion des templates Word de l'organisation" variant="success" />
              <div className="template-enterprise-section">
                <p>En tant qu'administrateur d'organisation, vous pouvez gérer les templates par défaut pour votre organisation.</p>
                <Button
                  variant="success"
                  icon={FaBuilding}
                  onClick={() => navigate('/enterprise')}
                >
                  Gérer les templates de l'organisation
                </Button>
              </div>
            </Card>
          )}

          {/* Guide Overlay */}
          <TutorialModal
            isOpen={isGuideOpen}
            onClose={() => setIsGuideOpen(false)}
            tutorialId="templates-guide"
            title="Guide des templates"
            message={(
              <div>
                <h3 style={{ marginTop: 0 }}>Sections et contenu</h3>
                <p>
                  Dans l'application, entrainez ou instruisez l'IA à saisir vos sections aux formats suivants:
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                  <p style={{ marginTop: 6 }}><strong>Pour une ligne</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Titre: Contenu`}
                    </pre>
                  </div>
                  <div>
                    <p style={{ marginTop: 6 }}><strong>Pour un paragraphe</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Titre
Contenu`}
                    </pre>
                  </div>
                </div>

                <h3>Placer les sections dans votre template Word</h3>
                <p>
                  Utilisez des <strong>balises entre accolades</strong> pour indiquer où le contenu doit s'insérer. Elle sera remplacée par le contenu de la section.
                </p>
                <h4>Lignes</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start' }}>
                  <div>
                    <p style={{ marginTop: 6 }}><strong>Exemple</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Indication: {indication}`}
                    </pre>
                  </div>
                  <div>
                    <p style={{ marginTop: 6 }}><strong>Résultat</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Indication: contenu de la section "indication"
`}
                    </pre>
                  </div>
                </div>

                <h4>Paragraphes</h4>
                <p>
                  Pour insérer une section paragraphe (avec du texte multi-lignes), utilisez la forme suivante:
                </p>
                <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`{#titreSection}
{#contenu}{.}

{/contenu}{/titreSection}`}
                </pre>
                <p> Chaque ligne sera insérée dans le template Word à la place de la balise <code>&#123;.&#125;</code>.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start', marginTop: 12 }}>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Exemple (titre: Diagnostic)</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`{#diagnosticSection}
Diagnostic
{#contenu}{.}

{/contenu}{/diagnosticSection}`}
                    </pre>
                  </div>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Résultat</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Diagnostic
Première ligne du diagnostic
Deuxième ligne du diagnostic
`}
                    </pre>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start' }}>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Exemple (titre: Status)</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`{#statusSection}
{#contenu}
- {.}{/contenu}{/statusSection}`}
                    </pre>
                  </div>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Résultat</strong></p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`- Première ligne du status
- Deuxième ligne du status
`}
                    </pre>
                  </div>
                </div>

                <h4>Tableaux</h4>
                <p>
                  Pour générer un tableau, placez une <strong>balise de tableau</strong> <code>&#123;@tableauTitre&#125;</code>. Les cellules du tableaux doivent être séparées par <strong>--- </strong>.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, alignItems: 'start' }}>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Exemple</strong></p>
                    <p>Cette section écrite selon ce schéma de tableau dans le rapport sur l'application...</p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`Traitement
Traitement --- Posologie --- Remarques
Aspirine cardio 100 mg --- 1-0-0-0 ---
Clopidogrel 75 mg --- 1-0-0-0 --- pendant 6 mois
Pantoprazole 40 mg --- 1-0-0-0 --- 
`}
                    </pre>
                  </div>
                  <div>
                    <p style={{ marginBottom: 6 }}><strong>Résultat</strong></p>
                    <p>...remplacerera <code>&#123;@tableauTraitement&#125;</code> dans le template par:</p>
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>

<table style={{ borderCollapse: 'collapse', width: '100%' }}>
  <thead>
    <tr>
      <th style={{ border: '1px solid #bbb', padding: '6px' }}>Traitement</th>
      <th style={{ border: '1px solid #bbb', padding: '6px' }}>Posologie</th>
      <th style={{ border: '1px solid #bbb', padding: '6px' }}>Remarques</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>Aspirine cardio 100 mg</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>1-0-0-0</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}></td>
    </tr>
    <tr>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>Clopidogrel 75 mg</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>1-0-0-0</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>pendant 6 mois</td>
    </tr>
    <tr>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>Pantoprazole 40 mg</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}>1-0-0-0</td>
      <td style={{ border: '1px solid #bbb', padding: '6px' }}></td>
    </tr>
  </tbody>
</table>

                    </pre>
                  </div>
                </div>

                <h3>Logo</h3>
                <p>
                  Insérez directement votre logo dans votre template Word personnalisé.
                </p>
                <p>
                  Ou utilisez le <strong>logo personnel</strong> uploadé dans cette page (il remplacera le logo par défaut à l'export).
                </p>
                <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`{#logoSection}
{%logo}
{/logoSection}`}
                </pre>
                

                <h3>Images</h3>
                <p>
                  Pour insérer une image dynamique (ex. le bulleyes des ETT) liée à un modèle de rapport, utilisez la forme suivante:
                </p>
                <pre style={{ whiteSpace: 'pre-wrap', background: '#f7f7f7', padding: 8, borderRadius: 6 }}>
{`{#imageSection}
{%image}
{/imageSection}`}
                </pre>

                <h3>Styles et mise en forme</h3>
                <p>
                  Le <strong>design et les styles de votre fichier Word</strong> (titres, polices, marges, etc.) sont conservés et appliqués lors de l'export.
                </p>
              </div>
            )}
          />

          {/* Template Builder Wizard */}
          <TemplateBuilderWizard
            isOpen={isBuilderOpen}
            onClose={() => setIsBuilderOpen(false)}
            onTemplateCreated={async (blob, config) => {
              // Handle template creation - upload to backend
              try {
                // Convert blob to File object
                const file = new File([blob], `${config.name || 'template'}.docx`, {
                  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                });
                await uploadTemplate(file);
                setIsBuilderOpen(false);
              } catch (error) {
                showNotification('Erreur lors de la sauvegarde du template', 'error');
              }
            }}
          />

          {/* Report Type Selection Modal */}
          {showReportTypeModal && (
            <div className="template-modal-overlay" onClick={cancelTemplateUpload}>
              <div className="template-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="template-modal-title">
                  Associer à un type de rapport
                </h3>
                <p className="template-modal-description">
                  Sélectionnez le type de rapport pour lequel ce template sera utilisé.
                </p>

                <Picker
                  id="report-type-upload-picker"
                  value={selectedReportTypeForUpload}
                  onChange={(e) => setSelectedReportTypeForUpload(e.target.value)}
                  disabled={templateLoading}
                >
                  {userReportTypes && userReportTypes
                    .filter(typeName => !templates.some(t => t.reportType === typeName))
                    .map((typeName, index) => (
                      <option key={`report-type-${index}-${typeName}`} value={typeName}>
                        {formatDisplayName(typeName)}
                      </option>
                    ))}
                </Picker>

                <div className="template-modal-actions">
                  <Button
                    variant="secondary"
                    onClick={cancelTemplateUpload}
                    disabled={templateLoading}
                  >
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    icon={FaFileUpload}
                    onClick={confirmTemplateUpload}
                    disabled={templateLoading}
                  >
                    {templateLoading ? 'Téléchargement...' : 'Uploader'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Templates;